import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DealerSearchContext } from '@rsc/contexts/DealerSearchContext';
import { LocaleContext } from '@rsc/contexts/LocaleContext';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';

import { GLOBAL_PRODUCT_COLLECTION } from '@rsc/utils/constants';
import breakpoints from '../breakpoints';
import ChevronUp from '../svgs/chevron-up.svg';
import ChevronDown from '../svgs/chevron-down.svg';
import NavSearch from './NavSearch';
import Collapsible from './Collapsible';
import LinkComponent from '../LinkComponent';
import IconProfile from '../svgs/user-profile-icon.svg';

const Wrapper = styled.div`
  position: absolute;
  width: 100%;
  background: ${({ theme }) => theme.colors.background};
  z-index: 1;
  transition: all 0.25s ease;
  padding-top: 100px;
  top: ${props => (props.mobileMenuActive ? null : '-120rem')};

  box-shadow: 0 2.8px 2.2px rgba(0, 0, 0, 0.02),
    0 6.7px 5.3px rgba(0, 0, 0, 0.028), 0 12.5px 10px rgba(0, 0, 0, 0.035),
    0 22.3px 17.9px rgba(0, 0, 0, 0.042), 0 41.8px 33.4px rgba(0, 0, 0, 0.05),
    0 100px 80px rgba(0, 0, 0, 0.07);

  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const NavMobileSubMenu = styled.div`
  section {
    padding-left: 1rem;
    margin-bottom: 0.75rem;
  }
  ul,
  li {
    padding-left: 0.5rem;
    margin: 0;
    list-style: none;
  }
  .second-level {
    margin-bottom: 0;
    padding-left: 0;
  }
  li {
    margin-bottom: 0.75rem;
    a {
      font-size: inherit;
      text-decoration: none;
      padding-left: 0.5rem;
      color: inherit;
      font-size: inherit;
      font-weight: inherit;

      &:hover {
        text-decoration: underline;
      }
    }
  }
  ul {
    margin-bottom: 1.25rem;
  }

  .Collapsible__trigger {
    display: flex;
    align-items: center;

    > a {
      width: 85%;
    }

    svg {
      flex: 1;
      fill: ${({ theme }) => theme.colors.defaultFont};
      margin: unset;
    }
  }
`;

const SearchContainer = styled.div`
  padding: 0 1rem;
  position: relative;
  z-index: 1;
`;

const NavMobileLink = styled(LinkComponent)`
  cursor: pointer;
  color: inherit;
  font-weight: inherit;
  background-repeat: no-repeat;
  background-position: right 1.5rem top 0.85rem;
  background-size: 1rem;
  display: flex;
  font-size: 1.1875rem;
  font-weight: inherit;
  padding: 0.5rem 1rem;
  text-decoration: none;
  margin-bottom: 0.5rem;

  ${({ $noArrow }) => $noArrow && `background: none;`}

  > span {
    flex: 1;
  }
`;

const NavMobileSubLink = styled(LinkComponent)`
  cursor: pointer;
  color: inherit;
  font-weight: inherit;
  background-repeat: no-repeat;
  background-position: right 1.6rem top 0.25rem;
  background-size: 0.85rem;
  display: flex;
  font-size: 1rem;
  padding-right: 1rem;
  text-decoration: none;

  svg {
    fill: ${({ theme }) => theme.colors.defaultFont};
    margin-top: 0.25rem;
  }

  > span {
    flex: 1;
  }
`;

// Move MegaMenu sections without title to top-level
const filterMegaMenu = megaMenu => {
  const mobileNavigation = [];

  megaMenu.items.forEach(megaMenuItem => {
    if (megaMenuItem.title) {
      mobileNavigation.push(megaMenuItem);
    }

    if (!megaMenuItem.title && megaMenuItem.items?.length > 0) {
      mobileNavigation.push(...megaMenuItem.items);
    }
  });

  return mobileNavigation;
};

function NavMobile({
  mobileMenuActive = false,
  navLinks = [],
  accountLinkComponent = {},
  flags = { clickableShopMenuItem: false },
  searchIndexes = [],
  getInternalLink = () => {},
  searchHitsPerPage = 5,
}) {
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';
  const AccountLink = accountLinkComponent;

  const { tt } = useUiComponentsInternationalContext();

  // check if using dealer context
  const dealerSearchContext = useContext(DealerSearchContext);
  const storedDealer = dealerSearchContext?.storedDealer ?? null;
  const toggleDealerSearch = dealerSearchContext?.toggleDealerSearch ?? null;

  return (
    <Wrapper mobileMenuActive={mobileMenuActive}>
      <SearchContainer>
        <NavSearch
          searchIndexes={searchIndexes}
          hitsPerPage={searchHitsPerPage}
        />
      </SearchContainer>
      {navLinks.map(navLink => {
        let mainNavLink = '#';

        if (navLink?.link?.internalLink || navLink?.link?.href) {
          mainNavLink = navLink?.link?.internalLink
            ? getInternalLink({
                ...navLink.link,
                reference: navLink.link?.internalLink,
              })
            : navLink?.link?.href;
        }

        // Dropdown or MegaMenu Items
        if (navLink.items) {
          return (
            <NavMobileSubMenu key={navLink.title}>
              <Collapsible
                transitionTime={100}
                trigger={
                  <>
                    <NavMobileLink
                      key={navLink.title}
                      to={mainNavLink}
                      onClick={e =>
                        flags.clickableShopMenuItem &&
                        // this comparison is used only in kurgo and has NO affect on sportdog
                        navLink.title === 'Shop'
                          ? ''
                          : e.preventDefault()
                      }
                    >
                      <span>{navLink.title}</span>
                    </NavMobileLink>
                    <ChevronDown width={20} height={20} />
                  </>
                }
                triggerWhenOpen={
                  <>
                    <NavMobileLink
                      key={navLink.title}
                      to={
                        navLink?.link?.internalLink
                          ? getInternalLink({
                              ...navLink.link,
                              reference: navLink?.link?.internalLink,
                            })
                          : navLink?.link?.href
                      }
                      onClick={e =>
                        // this comparison is used only in kurgo and has NO affect on sportdog
                        flags.clickableShopMenuItem && navLink.title === 'Shop'
                          ? ''
                          : e.preventDefault()
                      }
                      open
                    >
                      <span>{navLink.title}</span>
                    </NavMobileLink>
                    <ChevronUp width={20} height={20} />
                  </>
                }
              >
                {filterMegaMenu(navLink)?.map(section => {
                  let sectionSubLink = '#';

                  if (section?.link?.internalLink || section?.link?.href) {
                    sectionSubLink = section?.link?.internalLink
                      ? getInternalLink({
                          ...section.link,
                          reference: section?.link?.internalLink,
                        })
                      : section?.link?.href;
                  }

                  if (section.items) {
                    return (
                      <Collapsible
                        key={section.title}
                        transitionTime={100}
                        trigger={
                          <>
                            <NavMobileSubLink
                              key={section.title}
                              to={sectionSubLink}
                              onClick={e => e.preventDefault()}
                              aria-label={section.title}
                            >
                              <span>{section.title}</span>
                            </NavMobileSubLink>
                            <ChevronDown width={20} height={20} />
                          </>
                        }
                        triggerWhenOpen={
                          <>
                            <NavMobileSubLink
                              key={section.title}
                              to={sectionSubLink}
                              onClick={e => e.preventDefault()}
                              open
                              aria-label={section.title}
                            >
                              <span>{section.title}</span>
                            </NavMobileSubLink>
                            <ChevronUp width={20} height={20} />
                          </>
                        }
                        triggerTagName="section"
                      >
                        <ul>
                          {section.items?.length &&
                            section.items.map(
                              subSection =>
                                subSection.title !== 'Privacy Policy' &&
                                subSection.title !== 'Safe Again Program' && (
                                  <li key={subSection.title}>
                                    {subSection?.link?.href?.startsWith(
                                      'http'
                                    ) ? (
                                      <LinkComponent
                                        to={subSection.link?.href}
                                        target={
                                          subSection?.link?.blank
                                            ? `_blank`
                                            : `_self`
                                        }
                                      >
                                        {subSection.title}
                                      </LinkComponent>
                                    ) : (
                                      <LinkComponent
                                        to={
                                          subSection?.link?.internalLink
                                            ? getInternalLink({
                                                ...subSection.link,
                                                reference:
                                                  subSection?.link
                                                    ?.internalLink,
                                              })
                                            : subSection?.link?.href
                                        }
                                      >
                                        {subSection.title}
                                      </LinkComponent>
                                    )}
                                  </li>
                                )
                            )}
                          {(section.shopAll ||
                            // eslint-disable-next-line no-underscore-dangle
                            section?.link?.internalLink?._type ===
                              GLOBAL_PRODUCT_COLLECTION) && (
                            <li key={section.title}>
                              <LinkComponent
                                to={
                                  section?.link?.internalLink
                                    ? getInternalLink({
                                        ...section.link,
                                        reference: section?.link?.internalLink,
                                      })
                                    : section?.link?.href
                                }
                              >
                                {tt(s => s.headerCommon.shopAllTitleLink, {
                                  title: section.title,
                                })}
                              </LinkComponent>
                            </li>
                          )}
                          {section.title === 'Support' && (
                            <>
                              {storedDealer?.slug && (
                                <li>
                                  <LinkComponent
                                    to={
                                      currency === 'CAD'
                                        ? `/en-ca/dealer/${storedDealer.slug}`
                                        : `/dealer/${storedDealer.slug}`
                                    }
                                  >
                                    {tt(
                                      s => s.headerCommon.yourLocalDealerLink
                                    )}
                                  </LinkComponent>
                                </li>
                              )}
                              {!storedDealer && dealerSearchContext && (
                                <li>
                                  <LinkComponent
                                    href="#"
                                    onClick={toggleDealerSearch}
                                  >
                                    {tt(
                                      s => s.headerCommon.yourLocalDealerLink
                                    )}
                                  </LinkComponent>
                                </li>
                              )}
                            </>
                          )}
                        </ul>
                      </Collapsible>
                    );
                  }

                  return (
                    <ul key={section.title} className="second-level">
                      <li>
                        {section.link?.linkType === 'external' ? (
                          <LinkComponent
                            to={section.link?.href}
                            target={section?.link?.blank ? `_blank` : `_self`}
                          >
                            {section.title}
                          </LinkComponent>
                        ) : (
                          <LinkComponent
                            to={
                              section?.link?.internalLink
                                ? getInternalLink({
                                    ...section.link,
                                    reference: section?.link?.internalLink,
                                  })
                                : section?.link?.href
                            }
                          >
                            {section.title}
                          </LinkComponent>
                        )}
                      </li>
                    </ul>
                  );
                })}
              </Collapsible>
            </NavMobileSubMenu>
          );
        }

        // Static Menu Item
        return (
          <NavMobileLink to={navLink.link?.href} key={navLink.title} $noArrow>
            <span>{navLink.title}</span>
          </NavMobileLink>
        );
      })}
      <AccountLink
        to="/account"
        title={tt(s => s.headerCommon.myAccountLink)}
        aria-label={tt(s => s.headerCommon.myAccountAriaLabel)}
      >
        <IconProfile width={26} height={26} />
        <span>{tt(s => s.headerCommon.myAccountLink)}</span>
      </AccountLink>
    </Wrapper>
  );
}

NavMobile.propTypes = {
  accountLinkComponent: PropTypes.shape({}),
  mobileMenuActive: PropTypes.bool,
  navLinks: PropTypes.arrayOf(PropTypes.shape({})),
  flags: PropTypes.shape({
    clickableShopMenuItem: PropTypes.bool,
  }),
  searchIndexes: PropTypes.arrayOf(PropTypes.string),
  getInternalLink: PropTypes.func,
  searchHitsPerPage: PropTypes.number,
};

export default NavMobile;
